/**
 * Thin axios wrappers around the Candidates endpoints (`/candidates/*`),
 * shipped as Phase 1 of the candidate-attribution feature.
 *
 * All requests go through the shared `client` (base URL + Authorization
 * header injection live there). Tenant scope is enforced server-side by the
 * JWT middleware. Two server-side rules the UI mirrors:
 *
 *   - Creating/editing a candidate is gated to {ADMIN, ORG_MANAGER}.
 *   - Assigning `scopes` (the candidate's geographic reach) is ADMIN-only —
 *     an ORG_MANAGER sending a non-empty `scopes` gets a 403, which the UI
 *     avoids provoking by omitting the field, and surfaces inline if it
 *     happens anyway.
 *
 * **There is no DELETE.** Deactivation is hide-not-delete: historical knock
 * leans point at candidates by id and must still resolve to a name, so a
 * candidate is never removed — only hidden from pickers. Never add a
 * `deleteCandidate` here.
 *
 * Pattern mirrors `src/api/adminUsers.ts`: bare `client.<verb>` + typed
 * return values + async/await, no mock fixtures.
 */
import client from '@/api/client';
import { photoUrl } from '@/api/canvass';
import type {
  CandidateCreate,
  CandidateListItem,
  CandidateProfileUpdate,
  CandidateRead,
  CandidateUpdate,
} from '@/types/candidate';

// ---------------------------------------------------------------------------
// Request bodies re-exported for call sites — the canonical definitions live
// in `@/types/candidate` alongside the read shapes.
// ---------------------------------------------------------------------------

export type {
  CandidateCreate,
  CandidateUpdate,
  CandidateProfileUpdate,
  CandidateRead,
  CandidateListItem,
};

// ---------------------------------------------------------------------------
// Endpoints.
// ---------------------------------------------------------------------------

/**
 * List candidates in the caller's tenant. Read-gated on ANALYTICS_READ_ROLES,
 * not the provisioning roles — everyone who can see the map needs to see who
 * they're knocking for.
 *
 * Returns `CandidateListItem`, which is LIGHTER than `CandidateRead`: no
 * bio/headshot/urls/socials. Use `getCandidate` before editing.
 *
 * The server defaults to active-only. Pass `false` for the deactivated ones.
 *
 * ⚠️ There is **no reachable "all" mode.** The endpoint's own docstring claims
 * `?is_active=` returns everything; measured against the live dev API
 * (2026-07-15) that returns **422** — FastAPI cannot parse an empty string
 * into `Optional[bool]`, so the `None` branch is unreachable over the wire.
 * Callers wanting both slices must fetch twice and merge. Don't "fix" this by
 * sending an empty param.
 */
export async function listCandidates(isActive?: boolean): Promise<CandidateListItem[]> {
  const params = isActive === undefined ? undefined : { is_active: isActive };
  const res = await client.get<CandidateListItem[]>('/candidates', { params });
  return res.data;
}

/**
 * Fetch a single candidate by id — the full record, including socials.
 *
 * A cross-tenant id 404s rather than 403s, on purpose: a 403 would confirm the
 * row exists. Don't surface "forbidden" wording for a 404 here.
 */
export async function getCandidate(id: number): Promise<CandidateRead> {
  const res = await client.get<CandidateRead>(`/candidates/${id}`);
  return res.data;
}

/** Create a candidate in the caller's tenant. Gated {ADMIN, ORG_MANAGER}. */
export async function createCandidate(payload: CandidateCreate): Promise<CandidateRead> {
  const res = await client.post<CandidateRead>('/candidates', payload);
  return res.data;
}

/**
 * Patch an existing candidate. Any field left undefined is untouched; when
 * `socials` or `scopes` are present they replace that collection entirely.
 *
 * `slug` is not patchable — it is the stable identifier historical leans
 * resolve through. `CandidateUpdate` omits it by construction.
 */
export async function updateCandidate(
  id: number,
  payload: CandidateUpdate,
): Promise<CandidateRead> {
  const res = await client.patch<CandidateRead>(`/candidates/${id}`, payload);
  return res.data;
}

/**
 * Deactivate a candidate (soft — the row is retained so historical knock
 * leans still resolve to a name). This is the closest thing to a delete that
 * exists, and it is deliberately not one.
 */
export async function deactivateCandidate(id: number): Promise<CandidateRead> {
  const res = await client.post<CandidateRead>(`/candidates/${id}/deactivate`);
  return res.data;
}

/** Reactivate a previously-deactivated candidate via PATCH is_active=true. */
export async function reactivateCandidate(id: number): Promise<CandidateRead> {
  return updateCandidate(id, { is_active: true });
}

// ---------------------------------------------------------------------------
// Candidate self-service — the CANDIDATE's own profile
//
// These exist because the alternative was hand-written SQL on the production
// droplet, once per customer: that is literally how Jim Bosch's bio and both
// candidates' Facebook links got into the database, and why neither had a
// headshot until now.
//
// `updateCandidate` above is the STAFF door and can write strictly more. These
// three are gated on being the subject rather than on holding a role, and can
// reach only presentation. Do not "simplify" a call site by routing a
// candidate's edit through `updateCandidate` — they will get a 403, because
// PATCH /candidates/{id} is {ADMIN, ORG_MANAGER}.
// ---------------------------------------------------------------------------

/**
 * Patch the caller's OWN candidate profile. `PATCH /candidates/{id}/profile`,
 * gated CANDIDATE + a `relation='candidate'` link to this exact id.
 *
 * The server rejects any key outside `CandidateProfileUpdate` with a 422
 * rather than dropping it, so a form that accidentally spreads a whole
 * `CandidateRead` in here fails loudly instead of appearing to work. Send only
 * what changed.
 */
export async function updateMyCandidateProfile(
  id: number,
  payload: CandidateProfileUpdate,
): Promise<CandidateRead> {
  const res = await client.patch<CandidateRead>(`/candidates/${id}/profile`, payload);
  return res.data;
}

/**
 * Upload (or replace) a candidate's headshot. multipart/form-data, one `file`
 * field; returns the full updated record with `headshot_path` populated.
 *
 * Admitted for {ADMIN, ORG_MANAGER} acting on any candidate in their tenant,
 * and for a CANDIDATE acting on themselves — staff usually hold the photo
 * file, so both doors are real.
 *
 * JPEG / PNG / WebP, 5 MB max (`HEADSHOT_MAX_SIZE_BYTES`). Over-size and
 * wrong-type both come back 422 with a human-readable `detail`; surface it
 * rather than inventing your own wording, so the limit has one statement.
 */
export async function uploadCandidateHeadshot(
  id: number,
  file: File,
): Promise<CandidateRead> {
  const form = new FormData();
  form.append('file', file);
  const res = await client.post<CandidateRead>(`/candidates/${id}/headshot`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/**
 * Clear a candidate's headshot — column to null, file off disk. Idempotent:
 * no headshot returns 200 with an unchanged record, not a 404.
 *
 * A real delete in a domain that otherwise has none. That rule protects the
 * candidate ROW (historical leans must resolve to a name); a headshot
 * references nothing, and "I uploaded the wrong photo of my face" needs an
 * undo that isn't "upload a different one".
 */
export async function deleteCandidateHeadshot(id: number): Promise<CandidateRead> {
  const res = await client.delete<CandidateRead>(`/candidates/${id}/headshot`);
  return res.data;
}

/**
 * Full URL for a `headshot_path`. Headshots share the `/uploads/photos` static
 * mount with door photos — same directory, same base-URL derivation — so this
 * delegates to `photoUrl` rather than re-deriving it. Two implementations of
 * "where does the backend serve uploads from" is one too many.
 *
 * Note what that mount does NOT do: it applies no per-request authorization. A
 * headshot URL is readable by anyone who has it. That is fine for a photo the
 * candidate publishes on purpose, and is the reason nothing else goes there.
 */
export function headshotUrl(headshotPath: string): string {
  return photoUrl(headshotPath);
}
